<script setup lang="ts">
import {
  type Interval,
  addDays,
  addMonths,
  differenceInDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  lastDayOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { capitalize, some, chunk } from 'lodash-es';
import { Mousewheel } from 'swiper';
import type { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { LeftArrowIcon, RightArrowIcon } from '#shared/components/icons';
import { ref, watch, computed, nextTick } from 'vue';

const DAYS_PER_WEEK = 7;

const props = withDefaults(
  defineProps<{
    modelValue?: Date;
    intervalValue?: Interval;
    activeMonth?: Date;
    view?: 'week' | 'month';
    showMonthSelector?: boolean;
    whitelistedIntervals?: Interval[];
    intervalMode?: boolean;
  }>(),
  {
    modelValue: () => new Date(),
    intervalValue: undefined,
    activeMonth: () => new Date(),
    view: 'month',
    showMonthSelector: true,
    whitelistedIntervals: () => [],
    intervalMode: false,
  }
);
const emit = defineEmits<{
  (e: 'update:model-value', modelValue: Date): void;
  (e: 'update:activeMonth', activeMonth: Date): void;
  (e: 'update:intervalValue', interval: Interval): void;
}>();

const swiperRef = ref<SwiperClass>();
const activeMonth = ref(props.activeMonth);
const interval = ref(props.intervalValue);
const intervalSelectedDate = ref<Date>();
const hoveredDate = ref<Date>();

watch(interval, (value) => value && emit('update:intervalValue', value));
watch(
  () => props.intervalValue,
  (value) => {
    interval.value = value;
  }
);

async function slideToDate(date: Date) {
  const monthFirstDate = startOfMonth(date);
  updateActiveMonth(monthFirstDate);

  await nextTick();

  if (props.view === 'week') {
    const monthFirstWeekdayDate = startOfWeek(monthFirstDate);
    const index = Math.floor(
      Math.abs(differenceInDays(monthFirstWeekdayDate, date)) / DAYS_PER_WEEK
    );

    swiperRef.value?.slideTo(index, 0);
  } else if (props.view === 'month') swiperRef.value?.slideTo(1, 0);
}

function createInterval(date1: Date, date2: Date): Interval {
  return startOfDay(date1).getTime() >= startOfDay(date2).getTime()
    ? { start: date2, end: date1 }
    : { start: date1, end: date2 };
}

function onDayClick(date: Date) {
  if (isDayDisabled(date)) return;
  if (props.intervalMode) {
    if (intervalSelectedDate.value) {
      interval.value = createInterval(intervalSelectedDate.value, date);
      intervalSelectedDate.value = undefined;
    } else intervalSelectedDate.value = date;
  } else emit('update:model-value', date);
}

function onDayMouseEnter(date: Date) {
  hoveredDate.value = date;
}

watch(
  () => props.activeMonth,
  (newActiveMonth) => (activeMonth.value = newActiveMonth)
);

function getDayClasses(day: Date, viewMonthStartDate: Date) {
  const classes: string[] = [];
  if (isSameDay(day, props.modelValue)) classes.push('datepicker-day_today');
  if (!isSameMonth(day, viewMonthStartDate))
    classes.push('datepicker-day_another-month');

  if (props.intervalMode) {
    const displayInterval =
      intervalSelectedDate.value && hoveredDate.value
        ? createInterval(intervalSelectedDate.value, hoveredDate.value)
        : interval.value;
    if (displayInterval) {
      if (isSameDay(displayInterval.start, day))
        classes.push('datepicker-day_interval-start');
      else if (isSameDay(displayInterval.end, day))
        classes.push('datepicker-day_interval-end');
      else if (isWithinInterval(day, displayInterval))
        classes.push('datepicker-day_interval-middle');
    }
  }
  return classes;
}

function updateActiveMonth(newMonth: Date) {
  activeMonth.value = newMonth;
  emit('update:activeMonth', newMonth);
}

function onSwiper(swiper: SwiperClass) {
  swiperRef.value = swiper;
  slideToDate(props.modelValue);
}

function getWeekArrayOfMonth(monthDate: Date) {
  const monthFirstDate = startOfMonth(monthDate);
  const monthLastDate = endOfMonth(monthDate);
  const monthFirstWeekdayDate = startOfWeek(monthFirstDate);
  const monthLastWeekdayDate = lastDayOfWeek(monthLastDate);
  const days = eachDayOfInterval({
    start: monthFirstWeekdayDate,
    end: monthLastWeekdayDate,
  });
  return chunk(days, DAYS_PER_WEEK);
}

const activeMonthWeeks = computed(() => getWeekArrayOfMonth(activeMonth.value));

watch(
  () => props.modelValue,
  async (date) => {
    await nextTick();
    slideToDate(date);
  },
  { immediate: true }
);

watch(
  () => props.view,
  async () => {
    await nextTick();
    slideToDate(props.modelValue);
  }
);

type ViewedMonthData = {
  firstDate: Date;
  weeks: Date[][];
};

const activeMonths = computed<ViewedMonthData[]>(() => {
  const prevMonthFirstDate = subMonths(activeMonth.value, 1);
  const nextMonthFirstDate = addMonths(activeMonth.value, 1);
  const prevMonth = {
    firstDate: prevMonthFirstDate,
    weeks: getWeekArrayOfMonth(prevMonthFirstDate),
  };
  const currentMonth = {
    firstDate: activeMonth.value,
    weeks: activeMonthWeeks.value,
  };
  const nextMonth = {
    firstDate: nextMonthFirstDate,
    weeks: getWeekArrayOfMonth(nextMonthFirstDate),
  };
  return [prevMonth, currentMonth, nextMonth];
});

function slideToNextMonth() {
  updateActiveMonth(addMonths(activeMonth.value, 1));
  swiperRef.value?.slidePrev(0, false);
  swiperRef.value?.slideNext();
}

function slideToPrevMonth() {
  updateActiveMonth(subMonths(activeMonth.value, 1));
  swiperRef.value?.slideNext(0, false);
  swiperRef.value?.slidePrev();
}

function onSlideChange() {
  if (!swiperRef.value || props.view !== 'month') return;

  if (swiperRef.value.isBeginning) {
    updateActiveMonth(subMonths(activeMonth.value, 1));
    swiperRef.value?.slideNext(0);
  } else if (swiperRef.value.isEnd) {
    updateActiveMonth(addMonths(activeMonth.value, 1));
    swiperRef.value?.slidePrev(0);
  }
}

const weekDayNames = computed(() => {
  const start = startOfWeek(new Date());
  return [...Array(DAYS_PER_WEEK).keys()].map((i) =>
    format(addDays(start, i), 'EEEEEE')
  );
});

function isDayDisabled(day: Date) {
  return (
    props.whitelistedIntervals !== undefined &&
    some(
      props.whitelistedIntervals,
      (interval) => !isWithinInterval(day, interval)
    )
  );
}
</script>

<template>
  <div class="datepicker">
    <div v-if="props.showMonthSelector" class="datepicker__month-selector">
      <div class="datepicker__month">
        {{ capitalize(format(activeMonth, 'LLLL yyyy')) }}
      </div>
      <div class="datepicker__month-arrows">
        <AppButton
          variant="link"
          :icon-end="LeftArrowIcon"
          compact
          @click="slideToPrevMonth"
        />
        <AppButton
          variant="link"
          :icon-end="RightArrowIcon"
          compact
          @click="slideToNextMonth"
        />
      </div>
    </div>
    <Swiper
      :modules="[Mousewheel]"
      mousewheel
      auto-height
      :slides-per-view="1"
      :space-between="50"
      :threshold="10"
      @swiper="onSwiper"
      @slide-change-transition-end="onSlideChange"
    >
      <template v-if="props.view === 'week'">
        <SwiperSlide v-for="(week, i) in activeMonthWeeks" :key="i">
          <div class="datepicker__row">
            <div
              v-for="weekDay in weekDayNames"
              :key="weekDay"
              class="datepicker-day-heading"
            >
              {{ weekDay.toUpperCase() }}
            </div>
          </div>
          <div class="datepicker__rows">
            <div class="datepicker__row">
              <div
                v-for="day in week"
                :key="day.toDateString()"
                class="datepicker-day"
                :class="[
                  isSameDay(day, props.modelValue)
                    ? 'datepicker-day_today'
                    : '',
                  !isSameMonth(day, props.modelValue)
                    ? 'datepicker-day_another-month'
                    : '',
                ]"
                @click="onDayClick(day)"
                @mouseenter="onDayMouseEnter(day)"
              >
                <slot name="day" v-bind="day">
                  <div
                    :class="[
                      'datepicker-day__date',
                      { 'datepicker-day__date_disabled': isDayDisabled(day) },
                    ]"
                  >
                    {{ day.getDate() }}
                  </div>
                </slot>
              </div>
            </div>
          </div>
        </SwiperSlide>
      </template>
      <template v-else>
        <SwiperSlide
          v-for="month in activeMonths"
          :key="month.firstDate.toDateString()"
        >
          <div class="datepicker__row">
            <div
              v-for="weekDay in weekDayNames"
              :key="weekDay"
              class="datepicker-day-heading"
            >
              {{ weekDay.toUpperCase() }}
            </div>
          </div>
          <div class="datepicker__rows">
            <div
              v-for="(week, i) in month.weeks"
              :key="i"
              class="datepicker__row"
            >
              <div
                v-for="day in week"
                :key="day.toISOString()"
                class="datepicker-day"
                :class="getDayClasses(day, month.firstDate)"
                @click="() => onDayClick(day)"
                @mouseenter="onDayMouseEnter(day)"
              >
                <slot name="day" v-bind="day">
                  <div
                    :class="[
                      'datepicker-day__date',
                      { 'datepicker-day__date_disabled': isDayDisabled(day) },
                    ]"
                  >
                    {{ day.getDate() }}
                  </div>
                </slot>
              </div>
            </div>
          </div>
        </SwiperSlide>
      </template>
    </Swiper>
  </div>
</template>

<style lang="scss">
@import '#shared/assets/core.scss';

.datepicker {
  &__month-selector {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  &__month {
    /*font-size: 24px;*/
    font-size: 17px;
    font-weight: 500;
  }

  &__month-arrows {
    display: flex;
    gap: 12px;
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__row {
    display: flex;
    justify-content: stretch;
  }

  &-day-heading {
    flex: 1;
    display: flex;
    justify-content: center;
    width: 2rem;
    font-size: 13px;
  }

  &-day {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;

    &__date {
      position: relative;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      font-size: 17px;
      line-height: 21px;
      width: 2rem;
      height: 2rem;
      cursor: pointer;

      @include media-breakpoint-down(md) {
        font-size: 15px;
        line-height: 20px;
      }

      &_disabled {
        cursor: default;
        color: var(--app-color-black-secondary);
      }
    }

    &_another-month &__date {
      color: var(--app-color-black-secondary);
    }

    &_today &__date {
      border-radius: 50%;
      background: var(--app-gradient-primary);
      color: var(--app-color-white);
      min-width: 1em;
    }

    &_interval-start {
      background: var(--app-gradient-primary);
      border-radius: 20% 0 0 20%;
    }

    &_interval-middle {
      background: linear-gradient(
        to bottom,
        transparent 0%,
        transparent 9%,
        #e3e4f9 10%,
        #e3e4f9 90%,
        transparent 91%,
        transparent 100%
      );

      &:first-child {
        border-radius: 10px 0 0 10px;
      }

      &:last-child {
        border-radius: 0 10px 10px 0;
      }
    }

    &_interval-end {
      background: var(--app-gradient-primary);
      border-radius: 0 20% 20% 0;
    }

    &_interval-start &__date {
      color: var(--app-color-white);
    }

    &_interval-end &__date {
      color: var(--app-color-white);
    }
  }
}
</style>
